import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Toast } from 'primereact/toast';
import {
  buildContiguousUsCountyGeometry,
  buildRegionZctaGeometry,
  buildStateCountyGeometry,
  findContiguousUsCountyFips,
} from '../lib/usCountyMapGeometry';
import {
  filterResearchMarkets,
  formatCompactPopulation,
  formatResearchMarketRulePercent,
  getResearchMarketLatestRuleRatio,
  researchMarketPopulationTitle,
  researchMarketPopulationTone,
} from '../lib/researchMarkets';
import { apiJson } from '../services/api';
import { ZillowTabbedTimelineDemo } from './GrossYieldTimelineSection';
import { LaborTabbedChartDemo } from './LaborMarketSnapshotSection';
import MapSourceAuditDropdown from './MapSourceAuditDropdown';
import MapLayoutToggle from './MapLayoutToggle';
import PageLoader from './PageLoader';
import RegionGoogleMap from './RegionGoogleMap';
import { googleMapsApiKey } from '../lib/googleMapsLoader';
import { isNonDisclosureState } from '../lib/propertyDisclosure';
import { anchoredTooltipPlacement, usePinnedMapTooltip } from './mapTooltipInteraction';
import './FemaNriScreeningMapSection.css';
import './MapLayoutToggle.css';

const DIMENSIONS = { width: 1160, height: 730, padding: 6 };
const MARKET_LIMITS = [25, 50, 75, 100, 'all'];
const REGION_MAP_AUDIT_KEYS = ['zillow_market_snapshot', 'fema_nri_counties', 'fbi_ucr_return_a_counties'];
const RISK_COLORS = ['#dff3f4', '#b9e2e4', '#87cfd1', '#62bec1', '#3a9fa2'];
const CRIME_COLORS = ['#f3e8ff', '#d8b4fe', '#c084fc', '#9333ea', '#6b21a8'];
const MISSING_FILL = '#f8fafc';
const NON_CONTIGUOUS_MARKET_STATES = new Set(['AK', 'HI', 'PR', 'AS', 'GU', 'MP', 'VI']);
const ZILLOW_TOOLTIP_CACHE_LIMIT = 12;
const TOOLTIP_PLACEMENT_OPTIONS = {
  horizontalClampPercent: 21,
  flipThresholdPercent: 50,
};
const PINNED_TOOLTIP_MAX_WIDTH = 480;
const PINNED_TOOLTIP_VIEWPORT_GUTTER = 14;
const REGION_EVIDENCE_OPTIONS = [
  { key: 'for_sale', label: 'For sale' },
  { key: 'sold', label: 'Sold comps' },
  { key: 'rentals', label: 'Rental comps' },
];
const BOUNDARY_MODES = [
  { key: 'geoapify', label: 'Geoapify' },
  { key: 'overture', label: 'Overture' },
  { key: 'compare', label: 'Compare' },
];

function NonDisclosureIndicator({ stateAbbreviation }) {
  if (!isNonDisclosureState(stateAbbreviation)) return null;
  const label = `Non-disclosure state: ${String(stateAbbreviation).toUpperCase()}`;
  return (
    <span
      className="fema-screening-map__non-disclosure"
      aria-label={label}
      data-tooltip="Non-disclosure state"
      tabIndex="0"
    >
      <span aria-hidden="true">ND</span>
    </span>
  );
}

function featureBounds(feature) {
  const bounds = [Infinity, Infinity, -Infinity, -Infinity];
  const visit = (coordinates) => {
    if (!Array.isArray(coordinates)) return;
    if (typeof coordinates[0] === 'number') {
      bounds[0] = Math.min(bounds[0], coordinates[0]);
      bounds[1] = Math.min(bounds[1], coordinates[1]);
      bounds[2] = Math.max(bounds[2], coordinates[0]);
      bounds[3] = Math.max(bounds[3], coordinates[1]);
      return;
    }
    coordinates.forEach(visit);
  };
  visit(feature?.geometry?.coordinates);
  return bounds.every(Number.isFinite) ? bounds : null;
}

const COMP_GUIDANCE_TEXT = `FANNIE MAE + FREDDIE MAC COMPARABLE REVIEW CHECKLIST

Purpose
Use this as a screening and review aid. It is not an appraisal, lending decision, or substitute for the current agency guides or a licensed appraiser's market analysis.

1. Define the subject and its market
- Record property type, legal unit count, occupancy/use, bedrooms, bathrooms, gross living area, site, age, design, quality, condition, amenities, utilities, parking, lease terms, concessions, and any location influences.
- Define the market area from where competing demand comes. Do not choose a radius first and force properties into it.

2. Select the most comparable evidence
- Prefer properties competing for the same renters or buyers and with the most similar location, physical characteristics, legal use, and economic characteristics.
- Proximity is evidence, not a universal pass/fail rule. Measure and report distance and direction. If crossing neighborhood or market-area boundaries, explain why that market competes with the subject and analyze location differences.
- Do not select a comp merely because it supports a target value or rent. Verify material data using reliable, disinterested sources.

3. Rental comparables
- Use actual comparable rental market evidence. For a one-unit investment property, the legacy agency schedule is Fannie Mae Form 1007 / Freddie Mac Form 1000; under UAD 3.6, rental information generally appears in the URAR Rental Information section instead of a separate form.
- Compare physical structure, location, and lease terms. Analyze differences in unit type, bedrooms/bathrooms, living area, condition/quality, age, amenities, utilities, parking, furnishing, lease length, concessions, required fees, and whether the listing is a whole home or rent-by-room.
- Separate advertised asking rent from signed/achieved rent. Confirm listing status and date, remove duplicates, and identify stale or withdrawn listings. State whether required monthly fees are included.
- Reconcile the range into a supported market-rent conclusion. Explain which rentals deserve the most weight and why; do not simply average unmatched listings.
- Rental income used for mortgage qualification has separate documentation and calculation rules. A market-rent estimate alone does not establish qualifying income.

4. Sales comparables / ARV support
- Fannie Mae requires at least three closed comparable sales in the sales comparison approach. Current listings and contract offerings may provide additional support but do not replace the closed-sale minimum.
- Sales closed within the prior 12 months should generally be used, but the best comparable may be older. Explain older sales, competing-market sales, or other departures when they are the best available evidence.
- Analyze market conditions from each comparable's contract date to the appraisal effective date. Support either making or not making a time adjustment with market evidence.
- Derive adjustments from market reaction. Explain and support adjustments for location, site, view, condition, quality, size, room count, features, concessions, financing, and other material differences.
- Reconcile adjusted indications based on relative reliability and comparability; do not mechanically average them.

5. BRRRR use
- Treat an ARV as a prospective, as-completed scenario: define the planned scope and assumed finished condition before selecting renovated sales.
- Keep observed facts, inferred adjustments, and assumptions visibly separate. Preserve source, observation date, photos, selection/exclusion reasons, and reviewer notes.
- Stress-test rent, value, rehab cost, vacancy, expenses, refinance terms, and appraisal variance. Final lending/appraisal eligibility must be confirmed against current guides and lender overlays.

BROWSER AI ANALYSIS REQUEST
Analyze the subject and candidate comps I provide using the checklist above. Build separate tables for rental comps and sold comps. For every candidate, report distance/direction, recency, property/use match, bed/bath and size differences, condition/quality, location influences, lease or sale terms, fees/concessions, data gaps, adjustments that require market support, and an include/exclude recommendation with reasons. Do not invent missing facts or fixed agency limits. Distinguish asking rent from achieved rent and observed condition from assumed after-repair condition. Reconcile a supported range, identify the comps receiving greatest weight, list verification still needed, and state confidence and key risks.

Official references (verify current version before a lending decision)
Fannie Mae Selling Guide: https://selling-guide.fanniemae.com/
Fannie Mae appraisal forms (Form 1007): https://singlefamily.fanniemae.com/selling-servicing-guide-forms
Freddie Mac Seller/Servicer Guide: https://guide.freddiemac.com/
Freddie Mac Form 1000: https://guide.freddiemac.com/ci/okcsFattach/get/1001162_4`;

export function pinnedTooltipViewportLeft(markerRect, viewportWidth) {
  const width = Number(viewportWidth);
  const markerLeft = Number(markerRect?.left);
  const markerWidth = Number(markerRect?.width);
  if (![width, markerLeft, markerWidth].every(Number.isFinite) || width <= 0) return null;

  const tooltipWidth = Math.min(PINNED_TOOLTIP_MAX_WIDTH, width - (PINNED_TOOLTIP_VIEWPORT_GUTTER * 2));
  const halfTooltip = Math.max(0, tooltipWidth / 2);
  const minimumCenter = PINNED_TOOLTIP_VIEWPORT_GUTTER + halfTooltip;
  const maximumCenter = width - PINNED_TOOLTIP_VIEWPORT_GUTTER - halfTooltip;
  const markerCenter = markerLeft + (markerWidth / 2);
  return Math.min(maximumCenter, Math.max(minimumCenter, markerCenter));
}

function updateBoundedHistoryCache(cache, regionSlug, entry) {
  const next = { ...cache };
  delete next[regionSlug];
  next[regionSlug] = entry;
  while (Object.keys(next).length > ZILLOW_TOOLTIP_CACHE_LIMIT) {
    delete next[Object.keys(next)[0]];
  }
  return next;
}

function formatMonthYear(value) {
  if (!value) return 'Current';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function formatReleaseMonthYear(value) {
  if (!value) return 'Current release';
  const date = new Date(`1 ${value}`);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

const GeographyLayer = memo(function GeographyLayer({
  countyPaths,
  statePaths,
  onCountyClick,
  onCountyHover,
}) {
  return (
    <g
      className="fema-screening-map__geography"
      onClick={onCountyClick}
      onMouseOver={onCountyHover}
    >
      {countyPaths}
      {statePaths}
    </g>
  );
});

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function fillForCounty(county, layerType = 'fema') {
  if (layerType === 'fbi_crime') {
    if (!county || county.source_state !== 'complete') return MISSING_FILL;
    const rate = finiteNumber(county.crime_rate_per_100k);
    if (rate == null) return MISSING_FILL;
    const propertyCrime = county.metric_key === 'property_crime';
    const thresholds = propertyCrime ? [800, 1400, 2200, 3200] : [100, 200, 350, 550];
    if (rate >= thresholds[3]) return CRIME_COLORS[4];
    if (rate >= thresholds[2]) return CRIME_COLORS[3];
    if (rate >= thresholds[1]) return CRIME_COLORS[2];
    if (rate >= thresholds[0]) return CRIME_COLORS[1];
    return CRIME_COLORS[0];
  }
  if (!county || county.source_state !== 'applicable_with_value') return MISSING_FILL;
  const score = finiteNumber(county.risk_score);
  if (score == null) return MISSING_FILL;
  if (score >= 80) return RISK_COLORS[4];
  if (score >= 60) return RISK_COLORS[3];
  if (score >= 40) return RISK_COLORS[2];
  if (score >= 20) return RISK_COLORS[1];
  return RISK_COLORS[0];
}

export function femaRiskToneClass(county) {
  const rating = String(county?.risk_rating || '').toLowerCase();
  if (rating.includes('high')) return 'fema-screening-map__risk--high';
  if (rating.includes('low')) return 'fema-screening-map__risk--low';
  if (rating.includes('moderate')) return 'fema-screening-map__risk--middle';
  const score = finiteNumber(county?.risk_score);
  if (score == null) return 'fema-screening-map__risk--neutral';
  if (score >= 60) return 'fema-screening-map__risk--high';
  if (score <= 40) return 'fema-screening-map__risk--low';
  return 'fema-screening-map__risk--middle';
}

function formatScore(value) {
  const number = finiteNumber(value);
  return number == null ? '—' : number.toFixed(1);
}

function formatCompactCurrency(value) {
  const number = finiteNumber(value);
  if (number == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(number);
}

function femaCountyPopulationTitle(county, release) {
  if (county?.population == null || county.population === '') return 'County population unavailable';
  const numeric = Number(county.population);
  if (!Number.isFinite(numeric)) return 'County population unavailable';
  const vintage = release?.display_version ? ` · FEMA NRI ${release.display_version}` : ' · FEMA NRI';
  return `${Math.round(numeric).toLocaleString('en-US')} people${vintage} · county profile`;
}

function formatPercent(value) {
  const number = finiteNumber(value);
  return number == null ? '—' : `${Math.round(number * 100)}%`;
}

function hazardBarWidth(value) {
  const number = finiteNumber(value);
  return number == null ? '0%' : `${Math.max(2, Math.min(100, number * 100))}%`;
}

function countyLabel(county) {
  if (!county) return 'No FEMA county data';
  return `${county.name}, ${county.state_abbr}`;
}

function TopHazardDrivers({ county, showBars = false }) {
  if (!county?.top_hazards?.length) return null;
  return (
    <div className={`fema-screening-map__drivers${showBars ? ' fema-screening-map__drivers--bars' : ''}`}>
      <small>Top loss drivers</small>
      {county.top_hazards.map((hazard) => (
        <div key={hazard.key}>
          <strong>{hazard.label}</strong>
          {showBars ? (
            <i className="fema-screening-map__driver-track" aria-hidden="true">
              <i style={{ width: hazardBarWidth(hazard.loss_share) }} />
            </i>
          ) : null}
          <span>
            {formatCompactCurrency(hazard.expected_annual_loss)}
            {' · '}
            {formatPercent(hazard.loss_share)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function FemaNriScreeningMapSection({
  active = true,
  initialPayload,
  loading,
  error,
  viewTabs = null,
  marketSearch = '',
  onMarketSearchChange,
  onSelectedStateChange,
  selectedRegion = null,
  regionZctaPayload = null,
  regionLoading = false,
  regionError = '',
  onRegionMapOpen,
  onRegionMapPrefetch,
  onRegionMapBack,
  highlightedZcta = null,
  zctaFocusRequest = null,
  onRegionZctaStateChange,
  sourcingZones = [],
  zoneFocusRequest = null,
  onSaveZone,
  onUpdateZone,
  onDeleteZone,
  sourceAuditKeys = REGION_MAP_AUDIT_KEYS,
  showMarketFilter = false,
  showSourceAudit = false,
  showTitle = true,
  headerSupplement = null,
  detailsStacked = false,
  onDetailsLayoutToggle,
  selectedSearchTargetKeys = new Set(),
  onSearchTargetToggle,
  onRunSearchTarget,
  selectedSearchTargetCount = 0,
  selectionLimit = Infinity,
  getMarketSelectionId = (market) => market.id,
  isMarketSelectable = () => true,
  selectionPurpose = 'API Lab',
}) {
  const [payload, setPayload] = useState(initialPayload);
  const [selectedHazard, setSelectedHazard] = useState(
    initialPayload?.selected_hazard?.key || 'overall',
  );
  const [selectedCrimeMetric, setSelectedCrimeMetric] = useState('');
  const [marketLimit, setMarketLimit] = useState(25);
  const [selectedState, setSelectedState] = useState('');
  const tooltipResetKey = `${selectedState}:${selectedRegion?.region_slug || ''}:${initialPayload?.market_month_end || ''}`;
  const marketTooltip = usePinnedMapTooltip(tooltipResetKey);
  const countyTooltip = usePinnedMapTooltip(tooltipResetKey);
  const zctaTooltip = usePinnedMapTooltip(tooltipResetKey);
  const [googleMapUnavailable, setGoogleMapUnavailable] = useState('');
  // Bumped by a tooltip "Try again" so failed trend requests refetch in place.
  const [trendRetryNonce, setTrendRetryNonce] = useState(0);
  const [googleZctaAnchors, setGoogleZctaAnchors] = useState({});
  const [zipTooltipsDisabled, setZipTooltipsDisabled] = useState(false);
  const [zillowHistoryBySlug, setZillowHistoryBySlug] = useState({});
  const zillowHistoryCacheRef = useRef({});
  const [laborSnapshotBySlug, setLaborSnapshotBySlug] = useState({});
  const laborSnapshotCacheRef = useRef({});
  const [industryTrendsBySlug, setIndustryTrendsBySlug] = useState({});
  const industryTrendsCacheRef = useRef({});
  const [marketTrendView, setMarketTrendView] = useState('zillow');
  const [pinnedTooltipLeft, setPinnedTooltipLeft] = useState(null);
  const [switchingHazard, setSwitchingHazard] = useState(false);
  const [hazardError, setHazardError] = useState('');
  const [controlsOpen, setControlsOpen] = useState(false);
  const [regionEvidenceLayers, setRegionEvidenceLayers] = useState([]);
  const hasActiveRegionEvidence = regionEvidenceLayers.length > 0;
  const [regionSoldMonths, setRegionSoldMonths] = useState(6);
  const [regionEvidence, setRegionEvidence] = useState({ for_sale: [], sold: [], rentals: [], counts: {} });
  const [regionEvidenceLoading, setRegionEvidenceLoading] = useState(false);
  const [regionEvidenceLoaded, setRegionEvidenceLoaded] = useState(false);
  const [regionEvidenceError, setRegionEvidenceError] = useState('');
  const [compGuidanceOpen, setCompGuidanceOpen] = useState(false);
  const [boundaryExperiment, setBoundaryExperiment] = useState(null);
  const [boundaryMode, setBoundaryMode] = useState('');
  const [boundaryRunning, setBoundaryRunning] = useState(false);
  const [boundaryError, setBoundaryError] = useState('');
  const [boundaryReviewOpen, setBoundaryReviewOpen] = useState(false);
  const [boundaryWinner, setBoundaryWinner] = useState('');
  const [boundaryReviewNotes, setBoundaryReviewNotes] = useState({});
  const [blockReview, setBlockReview] = useState(null);
  const [blockReviewOpen, setBlockReviewOpen] = useState(false);
  const [blockReviewLoading, setBlockReviewLoading] = useState(false);
  const [blockReviewError, setBlockReviewError] = useState('');
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [blockZoneName, setBlockZoneName] = useState('');
  const [compGuidanceCopied, setCompGuidanceCopied] = useState(false);
  const [compGuidanceSaved, setCompGuidanceSaved] = useState(true);
  const [compGuidanceDraft, setCompGuidanceDraft] = useState(COMP_GUIDANCE_TEXT);
  const compGuidanceLoaded = useRef(false);
  const selectedRegionMarketId = selectedRegion?.market_id || selectedRegion?.region_id || selectedRegion?.id;
  const mapToast = useRef(null);
  const pendingEvidenceLayer = useRef(null);
  const controlsRef = useRef(null);
  const hoverFrameRef = useRef(null);
  const activeCountyFipsRef = useRef(null);
  const pendingCountyRef = useRef(null);
  const [overallCounties, setOverallCounties] = useState(
    initialPayload?.selected_hazard?.key === 'overall' ? initialPayload.counties || [] : [],
  );

  useEffect(() => {
    if (!initialPayload) return;
    setPayload(initialPayload);
    setSelectedHazard(initialPayload.selected_hazard?.key || 'overall');
    if (initialPayload.selected_hazard?.key === 'overall') {
      setOverallCounties(initialPayload.counties || []);
    }
  }, [initialPayload]);

  useEffect(() => {
    setRegionEvidenceLayers([]);
    setRegionEvidence({ for_sale: [], sold: [], rentals: [], counts: {} });
    setRegionEvidenceLoaded(false);
    setRegionEvidenceError('');
  }, [selectedRegion?.region_slug]);

  useEffect(() => {
    setBoundaryExperiment(null);
    setBoundaryMode('');
    setBoundaryError('');
    if (!selectedRegion || !selectedRegionMarketId) return undefined;
    let cancelled = false;
    apiJson(`/research/markets/${selectedRegionMarketId}/boundary-experiments/latest`)
      .then((result) => {
        if (!cancelled && result?.experiment_id) {
          setBoundaryExperiment(result);
          setBoundaryWinner(result.selected_winner || '');
          setBoundaryMode('compare');
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedRegion, selectedRegionMarketId]);

  useEffect(() => {
    setBlockReview(null);
    setBlockReviewOpen(false);
    setBlockReviewError('');
    setSelectedBlockIds([]);
    setBlockZoneName('');
  }, [selectedRegion?.region_slug]);

  const openBlockReview = async () => {
    if (blockReview) {
      setBlockReviewOpen((open) => !open);
      return;
    }
    setBlockReviewLoading(true);
    setBlockReviewError('');
    try {
      const result = await apiJson(`/research/markets/${selectedRegionMarketId}/census-block-review`);
      setBlockReview(result);
      setBlockReviewOpen(true);
      setRegionEvidenceLayers((current) => [...new Set([...current, 'for_sale', 'sold'])]);
    } catch (error) {
      setBlockReviewError(error?.message || 'Could not load Census blocks.');
    } finally {
      setBlockReviewLoading(false);
    }
  };

  const toggleBlock = (geoid) => {
    setSelectedBlockIds((current) => current.includes(geoid)
      ? current.filter((item) => item !== geoid)
      : [...current, geoid]);
  };

  const saveBlockZone = async () => {
    const selected = (blockReview?.blocks?.features || []).filter(
      (feature) => selectedBlockIds.includes(String(feature?.properties?.GEOID)),
    );
    if (!blockZoneName.trim() || !selected.length) return;
    setBlockReviewLoading(true);
    setBlockReviewError('');
    try {
      const result = await apiJson(`/research/markets/${selectedRegionMarketId}/census-block-review`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: blockZoneName.trim(), blocks: selected }),
      });
      mapToast.current?.show({
        severity: 'success', summary: 'Draft valuation zone saved',
        detail: `${result.name} combines ${selected.length} reviewed Census blocks.`, life: 4500,
      });
      setSelectedBlockIds([]);
      setBlockZoneName('');
      setBlockReviewOpen(false);
    } catch (error) {
      setBlockReviewError(error?.message || 'Could not save the draft valuation zone.');
    } finally {
      setBlockReviewLoading(false);
    }
  };

  const runBoundaryExperiment = async () => {
    const bounds = featureBounds(regionZctaPayload?.region_outline || geometry?.regionFeature);
    if (!bounds) {
      setBoundaryError('The region boundary is not available for this comparison.');
      return;
    }
    setBoundaryRunning(true);
    setBoundaryError('');
    try {
      const result = await apiJson(`/research/markets/${selectedRegionMarketId}/boundary-experiments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ bbox: bounds }),
      });
      setBoundaryExperiment(result);
      setBoundaryMode('compare');
      setBoundaryWinner(result.selected_winner || '');
      const errors = Object.values(result.source_errors || {});
      if (errors.length) setBoundaryError(errors.join(' '));
    } catch (error) {
      setBoundaryError(error?.message || 'Could not run the boundary comparison.');
    } finally {
      setBoundaryRunning(false);
    }
  };

  const saveBoundaryReview = async () => {
    if (!boundaryWinner) return;
    const candidates = [
      ...(boundaryExperiment?.comparison_stats?.geoapify?.top_five_lead_areas || []),
      ...(boundaryExperiment?.comparison_stats?.overture?.top_five_lead_areas || []),
    ].slice(0, 5);
    try {
      const result = await apiJson(`/research/boundary-experiments/${boundaryExperiment.experiment_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_winner: boundaryWinner,
          reviews: candidates.map((area) => ({
            area_label: area.name,
            preferred_source: boundaryWinner,
            notes: boundaryReviewNotes[area.feature_id] || '',
          })),
        }),
      });
      setBoundaryExperiment(result);
      setBoundaryReviewOpen(false);
      mapToast.current?.show({ severity: 'success', summary: 'Comparison saved', detail: 'The source decision and area notes were recorded.', life: 3500 });
    } catch (error) {
      setBoundaryError(error?.message || 'Could not save the comparison review.');
    }
  };

  const importBoundaryArea = async (source, area) => {
    try {
      await apiJson(`/research/boundary-experiments/${boundaryExperiment.experiment_id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, feature_id: area.feature_id, name: area.name }),
      });
      mapToast.current?.show({ severity: 'success', summary: 'Draft zone imported', detail: `${area.name} is now a draft valuation zone for human editing.`, life: 4500 });
    } catch (error) {
      setBoundaryError(error?.message || 'Could not import the draft valuation zone.');
    }
  };

  useEffect(() => {
    if (!selectedRegion || !selectedRegionMarketId || !hasActiveRegionEvidence) return undefined;
    let cancelled = false;
    setRegionEvidenceLoading(true);
    setRegionEvidenceError('');
    apiJson(`/research/markets/${selectedRegionMarketId}/map-property-evidence?sold_months=${regionSoldMonths}`)
      .then((result) => {
        if (!cancelled) {
          const nextEvidence = {
            for_sale: Array.isArray(result?.for_sale) ? result.for_sale : [],
            sold: Array.isArray(result?.sold) ? result.sold : [],
            rentals: Array.isArray(result?.rentals) ? result.rentals : [],
            counts: result?.counts || {},
          };
          setRegionEvidence(nextEvidence);
          setRegionEvidenceLoaded(true);
          const requestedLayer = pendingEvidenceLayer.current;
          pendingEvidenceLayer.current = null;
          if (requestedLayer && !nextEvidence.counts[requestedLayer]) {
            const label = REGION_EVIDENCE_OPTIONS.find((option) => option.key === requestedLayer)?.label || 'Property evidence';
            mapToast.current?.show({
              severity: 'info', life: 4500,
              content: () => <div className="fema-screening-map__empty-toast" role="status"><i className="pi pi-map-marker" aria-hidden="true" /><div><strong>No {label.toLowerCase()} found</strong><span>There is no retained {label.toLowerCase()} evidence for {selectedRegion.region_name}.</span></div></div>,
            });
          }
        }
      })
      .catch((requestError) => {
        if (!cancelled) setRegionEvidenceError(
          requestError?.message || 'Could not load retained property evidence.',
        );
      })
      .finally(() => {
        if (!cancelled) setRegionEvidenceLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedRegion, selectedRegionMarketId, hasActiveRegionEvidence, regionSoldMonths]);

  const toggleRegionEvidenceLayer = (layerKey) => {
    const isEnabled = regionEvidenceLayers.includes(layerKey);
    if (isEnabled) {
      setRegionEvidenceLayers((current) => current.filter((key) => key !== layerKey));
      return;
    }

    if (regionEvidenceLoaded && !regionEvidence.counts[layerKey]) {
      const label = REGION_EVIDENCE_OPTIONS.find((option) => option.key === layerKey)?.label || 'Property evidence';
      mapToast.current?.show({
        severity: 'info', life: 4500,
        content: () => <div className="fema-screening-map__empty-toast" role="status"><i className="pi pi-map-marker" aria-hidden="true" /><div><strong>No {label.toLowerCase()} found</strong><span>There is no retained {label.toLowerCase()} evidence for {selectedRegion.region_name}.</span></div></div>,
      });
    } else if (!regionEvidenceLoaded) pendingEvidenceLayer.current = layerKey;

    setRegionEvidenceLayers((current) => current.includes(layerKey) ? current : [...current, layerKey]);
  };
  const visibleRegionEvidence = useMemo(() => [
    ...(regionEvidenceLayers.includes('for_sale') ? regionEvidence.for_sale : []).map((item) => ({ ...item, evidence_type: 'for_sale' })),
    ...(regionEvidenceLayers.includes('sold') ? regionEvidence.sold : []).map((item) => ({ ...item, evidence_type: 'sold' })),
    ...(regionEvidenceLayers.includes('rentals') ? regionEvidence.rentals : []).map((item) => ({ ...item, evidence_type: 'rentals' })),
  ], [regionEvidence, regionEvidenceLayers]);

  const copyCompGuidance = async () => {
    await navigator.clipboard.writeText(compGuidanceDraft);
    setCompGuidanceCopied(true);
    window.setTimeout(() => setCompGuidanceCopied(false), 1800);
  };
  const saveCompGuidance = async () => {
    try {
      await apiJson('/identity/preferences/research.comp_guidance', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: compGuidanceDraft }),
      });
      setCompGuidanceSaved(true);
      mapToast.current?.show({ severity: 'success', summary: 'Guide saved', detail: 'Your edited comp guide was saved to your account.', life: 3000 });
    } catch (_error) {
      mapToast.current?.show({ severity: 'error', summary: 'Save failed', detail: 'The comp guide could not be saved to your account.', life: 4500 });
    }
  };

  const openCompGuidance = () => {
    setCompGuidanceOpen(true);
    if (compGuidanceLoaded.current) return;
    compGuidanceLoaded.current = true;
    apiJson('/identity/preferences/research.comp_guidance')
      .then((preference) => {
        if (typeof preference.value === 'string' && preference.value) setCompGuidanceDraft(preference.value);
      })
      .catch(() => undefined);
  };

  useEffect(() => () => {
    if (hoverFrameRef.current != null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(hoverFrameRef.current);
    }
  }, []);

  useEffect(() => {
    if (!controlsOpen) return undefined;
    const handlePointerDown = (event) => {
      if (controlsRef.current && !controlsRef.current.contains(event.target)) {
        setControlsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setControlsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [controlsOpen]);

  const hasPayload = Boolean(payload);
  const stateOptions = useMemo(() => {
    const fipsByAbbreviation = new Map();
    (overallCounties.length ? overallCounties : payload?.counties || []).forEach((county) => {
      const abbreviation = String(county.state_abbr || '').toUpperCase();
      const stateFips = String(county.geo_id || '').slice(0, 2);
      if (abbreviation && stateFips && !NON_CONTIGUOUS_MARKET_STATES.has(abbreviation)) {
        fipsByAbbreviation.set(abbreviation, stateFips);
      }
    });
    const marketCounts = new Map();
    (payload?.markets || []).forEach((market) => {
      const abbreviation = String(market.state_name || '').toUpperCase();
      marketCounts.set(abbreviation, (marketCounts.get(abbreviation) || 0) + 1);
    });
    return [...fipsByAbbreviation.entries()]
      .filter(([abbreviation]) => marketCounts.has(abbreviation))
      .map(([abbreviation, fips]) => ({
        abbreviation,
        fips,
        marketCount: marketCounts.get(abbreviation),
      }))
      .sort((left, right) => left.abbreviation.localeCompare(right.abbreviation));
  }, [overallCounties, payload?.counties, payload?.markets]);
  const selectedStateOption = stateOptions.find((option) => (
    option.abbreviation === selectedState
  ));
  const geometry = useMemo(() => {
    if (!hasPayload) return null;
    if (selectedRegion && regionZctaPayload) {
      return buildRegionZctaGeometry(
        selectedRegion.market_county_fips?.length
          ? selectedRegion.market_county_fips
          : [selectedRegion.marker_county_fips],
        regionZctaPayload,
        DIMENSIONS,
      );
    }
    return selectedStateOption
      ? buildStateCountyGeometry(selectedStateOption.fips, DIMENSIONS)
      : buildContiguousUsCountyGeometry(DIMENSIONS);
  }, [hasPayload, regionZctaPayload, selectedRegion, selectedStateOption]);
  const useGoogleRegionMap = Boolean(
    selectedRegion && geometry?.regionFeature && googleMapsApiKey() && !googleMapUnavailable,
  );
  useEffect(() => {
    setGoogleZctaAnchors({});
  }, [selectedRegion?.region_slug]);
  const countyByFips = useMemo(() => new Map(
    (payload?.counties || []).map((county) => [county.geo_id, county]),
  ), [payload?.counties]);
  const regionCountyFills = useMemo(() => Object.fromEntries(
    (regionZctaPayload?.region_counties?.features || []).map((feature) => [
      String(feature.id),
      fillForCounty(countyByFips.get(String(feature.id)), payload?.layer_type),
    ]),
  ), [countyByFips, payload?.layer_type, regionZctaPayload]);
  const countyShapeByFips = useMemo(() => new Map(
    (geometry?.counties || []).map((shape) => [shape.geoId, shape]),
  ), [geometry]);
  const overallCountyByFips = useMemo(() => new Map(
    overallCounties.map((county) => [county.geo_id, county]),
  ), [overallCounties]);
  const countyPathElements = useMemo(() => (geometry?.counties || []).map((shape) => {
    const county = countyByFips.get(shape.geoId);
    return (
      <path
        key={shape.geoId}
        d={shape.d}
        fill={fillForCounty(county, payload?.layer_type)}
        className="fema-screening-map__county"
        data-county-fips={shape.geoId}
      />
    );
  }), [countyByFips, geometry, payload?.layer_type]);
  const statePathElements = useMemo(() => (geometry?.states || []).map((shape) => (
    <path key={shape.geoId} d={shape.d} className="fema-screening-map__state-outline" />
  )), [geometry]);
  const regionClipId = selectedRegion
    ? `region-clip-${String(selectedRegion.region_slug || 'selected').replace(/[^a-zA-Z0-9_-]/g, '-')}`
    : null;
  const zctaById = useMemo(() => new Map(
    (geometry?.zctas || []).map((shape) => [shape.geoId, shape]),
  ), [geometry]);
  const zctaPathElements = useMemo(() => (geometry?.zctas || []).map((shape) => {
    const targetKey = `zcta:${shape.properties?.state_abbr || selectedRegion?.state_name}:${shape.geoId}`;
    return (
      <path
        key={shape.geoId}
        d={shape.d}
        className={`fema-screening-map__zcta${selectedSearchTargetKeys.has(targetKey) ? ' is-selected' : ''}${highlightedZcta === shape.geoId ? ' is-highlighted' : ''}`}
        data-zcta5={shape.geoId}
        tabIndex="0"
        role="button"
        aria-pressed={selectedSearchTargetKeys.has(targetKey)}
        aria-label={`${shape.properties.primary_locality ? `${shape.properties.primary_locality}, ` : ''}ZIP area ${shape.geoId}`}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
      />
    );
  }), [geometry, highlightedZcta, selectedRegion?.state_name, selectedSearchTargetKeys]);
  const rankedMarkets = useMemo(() => {
    if (selectedRegion) return [];
    const filtered = filterResearchMarkets(payload?.markets || [], marketSearch)
      .filter((market) => (
        market.coordinate_verified !== false
        && !NON_CONTIGUOUS_MARKET_STATES.has(String(market.state_name || '').toUpperCase())
        && finiteNumber(market.latitude) != null
        && finiteNumber(market.longitude) != null
      ));
    const withinMapArea = selectedState
      ? filtered.filter((market) => String(market.state_name || '').toUpperCase() === selectedState)
      : filtered;
    if (selectedState) return withinMapArea;
    return marketLimit === 'all' ? withinMapArea : withinMapArea.slice(0, marketLimit);
  }, [payload?.markets, marketLimit, marketSearch, selectedRegion, selectedState]);
  const marketPoints = useMemo(() => (geometry ? rankedMarkets.map((market) => {
    const point = geometry.projection([Number(market.longitude), Number(market.latitude)]);
    if (!point) return null;
    const markerCountyFips = market.marker_county_fips
      || findContiguousUsCountyFips(market.longitude, market.latitude);
    return {
      ...market,
      x: point[0],
      y: point[1],
      markerCounty: overallCountyByFips.get(markerCountyFips) || null,
    };
  }).filter(Boolean) : []), [geometry, overallCountyByFips, rankedMarkets]);
  const marketPointsInPaintOrder = useMemo(() => [...marketPoints].sort((left, right) => (
    Number(right.rank) - Number(left.rank)
  )), [marketPoints]);
  const hazards = payload?.hazards || [];
  const metroCount = finiteNumber(payload?.market_count) ?? (payload?.markets || []).length;
  const metroCountLabel = new Intl.NumberFormat('en-US').format(metroCount);
  const activeCounty = countyTooltip.activeId
    ? countyByFips.get(countyTooltip.activeId) || { geo_id: countyTooltip.activeId }
    : null;
  const activeZcta = zctaTooltip.activeId ? zctaById.get(zctaTooltip.activeId) : null;

  useEffect(() => {
    const regionSlug = marketTooltip.pinnedId;
    const cachedHistory = zillowHistoryCacheRef.current[regionSlug];
    if (!regionSlug || (cachedHistory && !cachedHistory.error)) return undefined;

    const storeHistoryEntry = (entry) => {
      const nextCache = updateBoundedHistoryCache(
        zillowHistoryCacheRef.current,
        regionSlug,
        entry,
      );
      zillowHistoryCacheRef.current = nextCache;
      setZillowHistoryBySlug(nextCache);
    };

    const loadingEntry = { loading: true, error: '', payload: null };
    storeHistoryEntry(loadingEntry);

    apiJson(`/research/markets/${regionSlug}/gross-yield-history`)
      .then((historyPayload) => {
        const readyEntry = { loading: false, error: '', payload: historyPayload };
        storeHistoryEntry(readyEntry);
      })
      .catch((requestError) => {
        const failedEntry = {
          loading: false,
          error: requestError?.message || 'Could not load Zillow history for this region.',
          payload: null,
        };
        storeHistoryEntry(failedEntry);
      });
    return undefined;
  }, [marketTooltip.pinnedId, trendRetryNonce]);

  useEffect(() => {
    const regionSlug = marketTooltip.pinnedId;
    const cachedSnapshot = laborSnapshotCacheRef.current[regionSlug];
    if (
      marketTrendView !== 'labor'
      || !regionSlug
      || (cachedSnapshot && !cachedSnapshot.error)
    ) return undefined;

    const storeSnapshotEntry = (entry) => {
      const nextCache = updateBoundedHistoryCache(
        laborSnapshotCacheRef.current,
        regionSlug,
        entry,
      );
      laborSnapshotCacheRef.current = nextCache;
      setLaborSnapshotBySlug(nextCache);
    };

    storeSnapshotEntry({ loading: true, error: '', payload: null });
    apiJson(`/research/markets/${regionSlug}/labor-market-snapshot`)
      .then((snapshotPayload) => {
        storeSnapshotEntry({ loading: false, error: '', payload: snapshotPayload });
      })
      .catch((requestError) => {
        storeSnapshotEntry({
          loading: false,
          error: requestError?.message || 'Could not load labor market data for this region.',
          payload: null,
        });
      });
    return undefined;
  }, [marketTooltip.pinnedId, marketTrendView, trendRetryNonce]);

  const requestIndustryTrends = useCallback((regionSlug) => {
    if (!regionSlug) return;
    const cachedTrends = industryTrendsCacheRef.current[regionSlug];
    if (cachedTrends && !cachedTrends.error) return;

    const storeTrendsEntry = (entry) => {
      const nextCache = updateBoundedHistoryCache(
        industryTrendsCacheRef.current,
        regionSlug,
        entry,
      );
      industryTrendsCacheRef.current = nextCache;
      setIndustryTrendsBySlug(nextCache);
    };

    storeTrendsEntry({ loading: true, error: '', payload: null });
    apiJson(`/research/markets/${regionSlug}/industry-trends`)
      .then((industryPayload) => {
        storeTrendsEntry({ loading: false, error: '', payload: industryPayload });
      })
      .catch((requestError) => {
        storeTrendsEntry({
          loading: false,
          error: requestError?.message || 'Could not load industry trends for this region.',
          payload: null,
        });
      });
  }, []);

  const handleCountyMouseOver = useCallback((event) => {
    if (selectedRegion || marketTooltip.pinnedId || countyTooltip.pinnedId || zctaTooltip.pinnedId) return;
    const countyFips = event.target?.dataset?.countyFips;
    if (!countyFips || countyFips === activeCountyFipsRef.current) return;
    pendingCountyRef.current = countyFips;

    if (hoverFrameRef.current != null) return;

    activeCountyFipsRef.current = countyFips;
    countyTooltip.show(countyFips);
    marketTooltip.clearHovered();

    if (typeof window.requestAnimationFrame !== 'function') return;
    hoverFrameRef.current = window.requestAnimationFrame(() => {
      hoverFrameRef.current = null;
      const pending = pendingCountyRef.current;
      if (!pending || pending === activeCountyFipsRef.current) return;
      activeCountyFipsRef.current = pending;
      countyTooltip.show(pending);
      marketTooltip.clearHovered();
    });
  }, [countyTooltip, marketTooltip, selectedRegion, zctaTooltip]);

  const handleCountyClick = useCallback((event) => {
    if (selectedRegion) return;
    const countyFips = event.target?.dataset?.countyFips;
    if (!countyFips) return;
    event.preventDefault();
    event.stopPropagation();
    marketTooltip.clearAll();
    countyTooltip.togglePinned(countyFips);
  }, [countyTooltip, marketTooltip, selectedRegion]);

  const handleZctaMouseOver = useCallback((event) => {
    if (!selectedRegion || zipTooltipsDisabled || zctaTooltip.pinnedId) return;
    const zcta5 = event.target?.dataset?.zcta5;
    if (!zcta5) return;
    marketTooltip.clearHovered();
    countyTooltip.clearHovered();
    zctaTooltip.show(zcta5);
  }, [countyTooltip, marketTooltip, selectedRegion, zipTooltipsDisabled, zctaTooltip]);

  const handleZctaClick = useCallback((event) => {
    if (!selectedRegion || zipTooltipsDisabled) return;
    const zcta5 = event.target?.dataset?.zcta5;
    if (!zcta5) return;
    event.preventDefault();
    event.stopPropagation();
    marketTooltip.clearAll();
    countyTooltip.clearAll();
    zctaTooltip.togglePinned(zcta5);
  }, [countyTooltip, marketTooltip, selectedRegion, zipTooltipsDisabled, zctaTooltip]);

  const clearTransientTooltip = useCallback(() => {
    if (hoverFrameRef.current != null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(hoverFrameRef.current);
    }
    hoverFrameRef.current = null;
    pendingCountyRef.current = null;
    activeCountyFipsRef.current = null;
    countyTooltip.clearHovered();
    marketTooltip.clearHovered();
    zctaTooltip.clearHovered();
  }, [countyTooltip, marketTooltip, zctaTooltip]);

  const dismissAllTooltips = useCallback(() => {
    clearTransientTooltip();
    marketTooltip.clearPinned();
    countyTooltip.clearPinned();
    zctaTooltip.clearPinned();
  }, [clearTransientTooltip, countyTooltip, marketTooltip, zctaTooltip]);

  useEffect(() => {
    if (!active) dismissAllTooltips();
  }, [active, dismissAllTooltips]);

  const rememberGoogleAnchor = useCallback((zcta5, anchor) => {
    if (!anchor) return;
    setGoogleZctaAnchors((current) => ({ ...current, [zcta5]: anchor }));
  }, []);
  const handleGoogleZctaHover = useCallback((zcta5, anchor) => {
    if (zipTooltipsDisabled || zctaTooltip.pinnedId) return;
    rememberGoogleAnchor(zcta5, anchor);
    marketTooltip.clearHovered();
    countyTooltip.clearHovered();
    zctaTooltip.show(zcta5);
  }, [countyTooltip, marketTooltip, rememberGoogleAnchor, zipTooltipsDisabled, zctaTooltip]);
  const setGooglePinnedAnchor = useCallback((zcta5, anchor) => {
    setGoogleZctaAnchors((current) => ({ ...current, [zcta5]: anchor }));
  }, []);
  const handleGoogleZctaFocused = useCallback((zcta5, anchor) => {
    setGooglePinnedAnchor(zcta5, anchor);
    marketTooltip.clearAll();
    countyTooltip.clearAll();
    if (zctaTooltip.pinnedId !== zcta5) zctaTooltip.togglePinned(zcta5);
  }, [countyTooltip, marketTooltip, setGooglePinnedAnchor, zctaTooltip]);
  useEffect(() => {
    onRegionZctaStateChange?.(selectedRegion
      ? { activeId: zctaTooltip.activeId, pinnedId: zctaTooltip.pinnedId }
      : { activeId: null, pinnedId: null });
  }, [onRegionZctaStateChange, selectedRegion, zctaTooltip.activeId, zctaTooltip.pinnedId]);
  useEffect(() => {
    // The SVG fallback has no camera to move; a table focus simply pins the ZIP.
    if (!zctaFocusRequest || !selectedRegion || useGoogleRegionMap) return;
    marketTooltip.clearAll();
    countyTooltip.clearAll();
    if (zctaTooltip.pinnedId !== zctaFocusRequest.zcta5) zctaTooltip.togglePinned(zctaFocusRequest.zcta5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zctaFocusRequest]);
  const handleGoogleZctaClick = useCallback((zcta5, anchor) => {
    if (zipTooltipsDisabled) return;
    rememberGoogleAnchor(zcta5, anchor);
    marketTooltip.clearAll();
    countyTooltip.clearAll();
    zctaTooltip.togglePinned(zcta5);
  }, [countyTooltip, marketTooltip, rememberGoogleAnchor, zipTooltipsDisabled, zctaTooltip]);

  const showMarketTooltip = useCallback((market) => {
    if (hoverFrameRef.current != null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(hoverFrameRef.current);
    }
    hoverFrameRef.current = null;
    pendingCountyRef.current = null;
    activeCountyFipsRef.current = null;
    marketTooltip.show(market.region_slug);
    countyTooltip.clearHovered();
  }, [countyTooltip, marketTooltip]);

  async function selectHazard(nextKey) {
    if (!nextKey || (nextKey === selectedHazard && !selectedCrimeMetric)) return;
    setSelectedCrimeMetric('');
    setSelectedHazard(nextKey);
    setSwitchingHazard(true);
    setHazardError('');
    countyTooltip.clearAll();
    try {
      const nextPayload = await apiJson(
        `/research/fema-nri-map/us?hazard=${encodeURIComponent(nextKey)}&include_markets=false`,
      );
      if (nextPayload.selected_hazard?.key === 'overall') {
        setOverallCounties(nextPayload.counties || []);
      }
      setPayload((current) => ({
        ...nextPayload,
        markets: current?.markets || [],
        market_count: current?.market_count,
        market_month_end: current?.market_month_end,
      }));
    } catch (requestError) {
      setHazardError(requestError?.message || 'Could not load that FEMA hazard.');
    } finally {
      setSwitchingHazard(false);
    }
  }

  async function selectCrimeMetric(nextKey) {
    if (!nextKey || nextKey === selectedCrimeMetric) return;
    setSelectedCrimeMetric(nextKey);
    setSwitchingHazard(true);
    setHazardError('');
    countyTooltip.clearAll();
    try {
      const nextPayload = await apiJson(
        `/research/fbi-ucr-map/us?metric=${encodeURIComponent(nextKey)}`,
      );
      setPayload((current) => ({
        ...nextPayload,
        counties: (nextPayload.counties || []).map((county) => ({
          ...county,
          metric_key: nextKey,
        })),
        hazards: current?.hazards || [],
        markets: current?.markets || [],
        market_count: current?.market_count,
        market_month_end: current?.market_month_end,
      }));
    } catch (requestError) {
      setSelectedCrimeMetric('');
      setHazardError(requestError?.message || 'Could not load FBI county crime data.');
    } finally {
      setSwitchingHazard(false);
    }
  }

  function resetControls() {
    setMarketLimit(25);
    setSelectedState('');
    onSelectedStateChange?.('');
    onRegionMapBack?.();
    onMarketSearchChange?.('');
    if (selectedHazard !== 'overall') selectHazard('overall');
    else if (selectedCrimeMetric) selectHazard('overall');
  }

  if (loading || (!payload && !error)) {
    return (
      <section
        className="fema-screening-map fema-screening-map--state fema-screening-map--loading"
        data-testid="fema-loading-shell"
      >
        {viewTabs}
        <PageLoader compact label="Loading FEMA map" />
      </section>
    );
  }
  if (error || !payload) {
    return (
      <section className="fema-screening-map fema-screening-map--state">
        <strong>FEMA screening map unavailable</strong>
        <span>{error}</span>
      </section>
    );
  }

  return (
    <section className="fema-screening-map">
      <Toast ref={mapToast} position="top-right" className="fema-screening-map__toast" />
      <header className="fema-screening-map__header">
        <div className="fema-screening-map__header-top">
          <div>
            <p className="fema-screening-map__eyebrow fema-screening-map__live-data">
              <span className="fema-screening-map__live-dot" aria-hidden="true" />
              <span>Live data - {metroCountLabel} metros</span>
            </p>
            {showTitle ? <h2>Where do top cash-flow markets overlap with modeled hazard risk?</h2> : null}
          </div>
          {headerSupplement ? <div className="fema-screening-map__header-supplement">{headerSupplement}</div> : null}
          <div className="fema-screening-map__release">
            <div>
              <a href="https://www.zillow.com/research/data/" target="_blank" rel="noreferrer">
                <i className="fema-screening-map__zillow-mark" aria-hidden="true"><img src="https://upload.wikimedia.org/wikipedia/commons/3/3e/Zillow_2024.svg" alt="" /></i>
                <span>Zillow coverage</span>
              </a>
              <strong>{formatMonthYear(payload.market_month_end)}</strong>
            </div>
            <div>
              <a href={payload.source?.documentation_url || payload.source?.source_url || 'https://hazards.fema.gov/nri/'} target="_blank" rel="noreferrer">
                <i className="fema-screening-map__fema-mark" aria-hidden="true"><img src="https://upload.wikimedia.org/wikipedia/commons/6/67/FEMA_logo.svg" alt="" /></i>
                <span>FEMA NRI</span>
              </a>
              <strong>{formatReleaseMonthYear(payload.release?.display_version)}</strong>
            </div>
          </div>
        </div>
        {viewTabs}
      </header>

      {hazardError ? <p className="fema-screening-map__error">{hazardError}</p> : null}

      <div className={`fema-screening-map__stage ${switchingHazard ? 'is-loading' : ''}${selectedRegion ? ' is-region-view' : ''}`}>
        {selectedRegion && regionLoading ? (
          <div className="fema-screening-map__region-transition" role="status" aria-live="polite">
            <PageLoader compact label={`Loading ${selectedRegion.region_name} region map`} />
            <strong>Opening {selectedRegion.region_name}</strong>
            <span>Loading detailed ZIP boundaries…</span>
          </div>
        ) : null}
        {onDetailsLayoutToggle ? (
          <MapLayoutToggle stacked={detailsStacked} onToggle={onDetailsLayoutToggle} />
        ) : null}
        <div className="fema-screening-map__floating-controls" ref={controlsRef}>
          <button
            type="button"
            className="fema-screening-map__controls-trigger"
            aria-label="Map controls"
            aria-expanded={controlsOpen}
            aria-controls="fema-screening-map-controls"
            title="Map controls"
            onClick={() => {
              dismissAllTooltips();
              setControlsOpen((open) => !open);
            }}
          >
            <i className="pi pi-sliders-h" aria-hidden="true" />
            <i className={`pi ${controlsOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true" />
          </button>

          <div
            id="fema-screening-map-controls"
            className="fema-screening-map__controls-popover"
            hidden={!controlsOpen}
          >
            <div className="fema-screening-map__controls-popover-head">
              <strong>Map controls</strong>
              <button type="button" onClick={resetControls}>Reset</button>
            </div>
            <div className="fema-screening-map__hazards" aria-label="Primary map layer">
              <button
                type="button"
                className={!selectedCrimeMetric ? 'is-active' : ''}
                aria-pressed={!selectedCrimeMetric}
                onClick={() => selectHazard('overall')}
              >
                All FEMA
              </button>
              <button
                type="button"
                className={selectedCrimeMetric === 'violent_crime' ? 'is-active' : ''}
                aria-pressed={selectedCrimeMetric === 'violent_crime'}
                onClick={() => selectCrimeMetric('violent_crime')}
              >
                Violent crime
              </button>
            </div>
            <label className="fema-screening-map__map-area">
              <span>Map area</span>
              <select
                aria-label="Map area"
                value={selectedRegion ? `region:${selectedRegion.region_slug}` : selectedState}
                onChange={(event) => {
                  const nextState = event.target.value;
                  if (nextState.startsWith('region:')) return;
                  if (selectedRegion) onRegionMapBack?.();
                  setSelectedState(nextState);
                  onSelectedStateChange?.(nextState);
                  dismissAllTooltips();
                  setControlsOpen(false);
                }}
              >
                <option value="">All United States</option>
                {selectedRegion ? (
                  <option value={`region:${selectedRegion.region_slug}`}>
                    {selectedRegion.region_name} · Region
                  </option>
                ) : null}
                {stateOptions.map((option) => (
                  <option key={option.abbreviation} value={option.abbreviation}>
                    {option.abbreviation} · {option.marketCount} markets
                  </option>
                ))}
              </select>
            </label>
            {!selectedRegion ? <div className="fema-screening-map__dataset-selectors">
              <label className="fema-screening-map__more-hazards">
                <span>More FEMA hazards</span>
                <select
                  value={selectedCrimeMetric || selectedHazard === 'overall' ? '' : selectedHazard}
                  onChange={(event) => selectHazard(event.target.value)}
                >
                  <option value="">Choose another hazard</option>
                  {hazards.map((hazard) => (
                    <option key={hazard.key} value={hazard.key}>{hazard.label}</option>
                  ))}
                </select>
              </label>
              <label className="fema-screening-map__crime-layer">
                <span>FBI county crime</span>
                <select
                  aria-label="FBI county crime"
                  value={selectedCrimeMetric}
                  onChange={(event) => selectCrimeMetric(event.target.value)}
                >
                  <option value="">Choose crime measure</option>
                  <option value="violent_crime">Violent crime rate</option>
                  <option value="property_crime">Property crime rate</option>
                </select>
              </label>
            </div> : null}
            <div className="fema-screening-map__market-tools">
              {showMarketFilter && !selectedRegion ? (
                <label className="fema-screening-map__market-search">
                  <span>Filter markets</span>
                  <span className="fema-screening-map__market-search-field">
                    <i className="pi pi-search" aria-hidden="true" />
                    <input
                      type="search"
                      value={marketSearch}
                      onChange={(event) => onMarketSearchChange?.(event.target.value)}
                      placeholder="Region name"
                      aria-label="Filter markets by region name"
                    />
                  </span>
                </label>
              ) : null}
              {selectedRegion ? (
                <>
                  <label className="fema-screening-map__zip-tooltip-toggle">
                    <span><strong>Property pin mode</strong><small>Turn off ZIP hover cards while reviewing properties</small></span>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={zipTooltipsDisabled}
                      onChange={(event) => {
                        const disabled = event.target.checked;
                        setZipTooltipsDisabled(disabled);
                        if (disabled) zctaTooltip.clearAll();
                      }}
                      aria-label="Disable ZIP area tooltips"
                    />
                  </label>
                  {selectedRegionMarketId ? <div className="fema-screening-map__region-evidence-controls">
                    <span>Evidence layers</span>
                    <div role="group" aria-label="Region evidence layers">
                      {REGION_EVIDENCE_OPTIONS.map((option) => (
                        <label key={option.key}>
                          <span>
                            <i className={`is-${option.key.replace('_', '-')}`} aria-hidden="true" />
                            {option.label}
                          </span>
                          <input
                            type="checkbox"
                            role="switch"
                            checked={regionEvidenceLayers.includes(option.key)}
                            onChange={() => toggleRegionEvidenceLayer(option.key)}
                            aria-label={option.label}
                          />
                        </label>
                      ))}
                    </div>
                    {regionEvidenceLayers.includes('sold') ? (
                      <label className="fema-screening-map__sold-timeframe">
                        <span>Sold within</span>
                        <select
                          aria-label="Sold comp timeframe"
                          value={regionSoldMonths}
                          onChange={(event) => setRegionSoldMonths(Number(event.target.value))}
                        >
                          {[3, 6, 12].map((months) => <option key={months} value={months}>{months} months</option>)}
                        </select>
                      </label>
                    ) : null}
                    <div className="fema-screening-map__region-evidence-footer">
                      <small>
                        {regionEvidenceLoading
                          ? 'Loading retained evidence…'
                          : regionEvidenceError || REGION_EVIDENCE_OPTIONS.filter((option) => regionEvidenceLayers.includes(option.key)).map((option) => `${regionEvidence.counts[option.key] || 0} ${option.key === 'rentals' ? 'rental comps' : option.label.toLowerCase()}`).join(' · ') || `ZIP areas in ${selectedRegion.region_name}`}
                      </small>
                      <button
                        type="button"
                        className="fema-screening-map__comp-guidance-trigger"
                        aria-label="Comp review guide"
                        data-tooltip="Comp review guide"
                        onClick={openCompGuidance}
                      >
                        <i className="pi pi-book" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="fema-screening-map__boundary-experiment">
                      <div className="fema-screening-map__boundary-heading">
                        <span>Assisted comp zone</span>
                        <button type="button" onClick={openBlockReview} disabled={blockReviewLoading}>
                          {blockReviewLoading ? 'Loading…' : blockReviewOpen ? 'Hide blocks' : blockReview ? 'Show blocks' : 'Build with blocks'}
                        </button>
                      </div>
                      {blockReviewOpen && blockReview ? <div className="fema-screening-map__block-builder">
                        <small>Select touching Census blocks around a genuinely similar market area. The highlighted starting window contains {blockReview.block_count} blocks and {Object.values(blockReview.evidence_counts || {}).reduce((sum, count) => sum + count, 0)} retained evidence points.</small>
                        <div className="fema-screening-map__block-reference-links">
                          <a href="https://cityofmarion.in.gov/government/departments/city-planning/zoning-information" target="_blank" rel="noreferrer">Marion zoning map</a>
                          <a href="https://cityofmarion.in.gov/government/departments/city-development/neighborhood-associations" target="_blank" rel="noreferrer">Neighborhood maps</a>
                        </div>
                        <label><span>Draft zone name</span><input value={blockZoneName} maxLength={120} onChange={(event) => setBlockZoneName(event.target.value)} placeholder="Example: Northeast Marion R2" /></label>
                        <div className="fema-screening-map__block-actions"><b>{selectedBlockIds.length} selected</b><button type="button" onClick={() => setSelectedBlockIds([])} disabled={!selectedBlockIds.length}>Clear</button><button type="button" onClick={saveBlockZone} disabled={blockReviewLoading || !selectedBlockIds.length || !blockZoneName.trim()}>Save draft zone</button></div>
                      </div> : null}
                      {blockReviewError ? <small className="fema-screening-map__boundary-error">{blockReviewError}</small> : null}
                    </div>
                    <div className="fema-screening-map__boundary-experiment">
                      <div className="fema-screening-map__boundary-heading">
                        <span>Draft boundary test</span>
                        <button type="button" onClick={runBoundaryExperiment} disabled={boundaryRunning}>
                          {boundaryRunning ? 'Comparing…' : boundaryExperiment ? 'Run again' : 'Run comparison'}
                        </button>
                      </div>
                      {boundaryExperiment ? <>
                        <div className="fema-screening-map__boundary-modes" role="group" aria-label="Boundary comparison map mode">
                          {BOUNDARY_MODES.map((mode) => <button
                            type="button"
                            key={mode.key}
                            className={boundaryMode === mode.key ? 'is-active' : ''}
                            aria-pressed={boundaryMode === mode.key}
                            onClick={() => setBoundaryMode(boundaryMode === mode.key ? '' : mode.key)}
                          >{mode.label}</button>)}
                        </div>
                        <div className="fema-screening-map__boundary-stats">
                          {['geoapify', 'overture'].map((source) => {
                            const stats = boundaryExperiment.comparison_stats?.[source];
                            return <small key={source}><b>{source === 'geoapify' ? 'Geoapify' : 'Overture'}</b>{stats ? `${stats.polygon_count} areas · ${stats.coverage_percent}% evidence coverage` : ' unavailable'}</small>;
                          })}
                        </div>
                        {(boundaryExperiment.comparison_stats?.geoapify?.polygon_count || 0) + (boundaryExperiment.comparison_stats?.overture?.polygon_count || 0) > 0
                          ? <button type="button" className="fema-screening-map__boundary-review-button" onClick={() => setBoundaryReviewOpen(true)}>Review five lead areas</button>
                          : <small>No candidate neighborhood polygons were returned. This is a valid “neither” result for this market.</small>}
                      </> : null}
                      {boundaryError ? <small className="fema-screening-map__boundary-error">{boundaryError}</small> : null}
                    </div>
                  </div> : (
                    <small className="fema-screening-map__region-evidence-unavailable">
                      Property evidence layers are available for ranked research markets.
                    </small>
                  )}
                </>
              ) : (
                <label className="fema-screening-map__market-limit">
                  <span>Show markets</span>
                  <select
                    aria-label="Show top markets"
                    value={marketLimit}
                    disabled={Boolean(selectedState)}
                    onChange={(event) => setMarketLimit(
                      event.target.value === 'all' ? 'all' : Number(event.target.value),
                    )}
                  >
                    {MARKET_LIMITS.map((limit) => (
                      <option key={limit} value={limit}>{limit === 'all' ? 'All' : `Top ${limit}`}</option>
                    ))}
                  </select>
                  {selectedState ? <small>Showing every ranked market in {selectedState}</small> : null}
                </label>
              )}
            </div>
          </div>
        </div>

        {useGoogleRegionMap ? (
          <RegionGoogleMap
            key={selectedRegion.region_slug}
            regionFeature={regionZctaPayload?.region_outline || geometry.regionFeature}
            zctas={geometry.zctas}
            stateAbbr={selectedRegion.state_name}
            selectedTargetKeys={selectedSearchTargetKeys}
            hoveredZcta={zctaTooltip.hoveredId}
            pinnedZcta={zctaTooltip.pinnedId}
            highlightedZcta={highlightedZcta}
            focusRequest={zctaFocusRequest}
            countyFeatures={regionZctaPayload?.region_counties}
            countyFills={regionCountyFills}
            showCountyLayer={false}
            zipInteractionsEnabled={!zipTooltipsDisabled}
            evidencePoints={visibleRegionEvidence}
            boundaryExperiment={boundaryExperiment}
            boundaryExperimentMode={boundaryMode}
            censusBlocks={blockReview?.blocks || null}
            censusBlockMode={blockReviewOpen}
            selectedCensusBlockIds={selectedBlockIds}
            onToggleCensusBlock={toggleBlock}
            dimensions={DIMENSIONS}
            label={`${selectedRegion.region_name} street map with ZIP areas`}
            onZctaHover={handleGoogleZctaHover}
            onZctaLeave={(zcta5) => zctaTooltip.hide(zcta5)}
            onZctaClick={handleGoogleZctaClick}
            onZctaFocused={handleGoogleZctaFocused}
            onPinnedAnchorChange={setGooglePinnedAnchor}
            onBackgroundClick={dismissAllTooltips}
            onViewportStart={clearTransientTooltip}
            zones={sourcingZones}
            zoneFocusRequest={zoneFocusRequest}
            onSaveZone={onSaveZone}
            onUpdateZone={onUpdateZone}
            onDeleteZone={onDeleteZone}
            onUnavailable={setGoogleMapUnavailable}
          />
        ) : (
        <svg
          viewBox={`0 0 ${DIMENSIONS.width} ${DIMENSIONS.height}`}
          role="img"
          aria-label={selectedRegion
            ? `${selectedRegion.region_name} region map with ZIP areas`
            : `FEMA ${payload.selected_hazard?.label || 'overall risk'} county map for ${selectedState || 'the United States'} with ranked cash-flow markets`}
          onMouseLeave={clearTransientTooltip}
          onClick={(event) => {
            if (event.target === event.currentTarget || event.target.classList?.contains('fema-screening-map__background')) {
              dismissAllTooltips();
            }
          }}
        >
          {selectedRegion && geometry?.states?.[0]?.d ? (
            <defs>
              <clipPath id={regionClipId}>
                <path d={geometry.states[0].d} />
              </clipPath>
            </defs>
          ) : null}
          <rect
            width={DIMENSIONS.width}
            height={DIMENSIONS.height}
            rx="18"
            className="fema-screening-map__background"
            onMouseEnter={clearTransientTooltip}
            onMouseMove={clearTransientTooltip}
          />
          <GeographyLayer
            countyPaths={countyPathElements}
            statePaths={selectedRegion ? [] : statePathElements}
            onCountyClick={handleCountyClick}
            onCountyHover={handleCountyMouseOver}
          />
          {selectedRegion ? (
            <g
              className={`fema-screening-map__zctas${zipTooltipsDisabled ? ' is-noninteractive' : ''}`}
              clipPath={`url(#${regionClipId})`}
              onMouseOver={handleZctaMouseOver}
              onClick={handleZctaClick}
            >
              {zctaPathElements}
              {(geometry?.zctas || []).map((shape) => shape.centroid ? (
                <text
                  key={`label-${shape.geoId}`}
                  x={shape.centroid[0]}
                  y={shape.centroid[1]}
                  className="fema-screening-map__zcta-label"
                >
                  {shape.properties.primary_locality ? (
                    <tspan x={shape.centroid[0]} dy="-2">{shape.properties.primary_locality}</tspan>
                  ) : null}
                  <tspan x={shape.centroid[0]} dy={shape.properties.primary_locality ? '11' : '3'}>{shape.geoId}</tspan>
                </text>
              ) : null)}
            </g>
          ) : null}
          {selectedRegion ? <g className="fema-screening-map__region-outline">{statePathElements}</g> : null}
          {marketPointsInPaintOrder.map((market) => (
            <g
              key={market.region_slug}
              className="fema-screening-map__market"
              transform={`translate(${market.x} ${market.y})`}
              tabIndex="0"
              role="button"
              aria-pressed={marketTooltip.pinnedId === market.region_slug}
              aria-label={`Rank ${market.rank}: ${market.region_name}, ${formatResearchMarketRulePercent(getResearchMarketLatestRuleRatio(market))}. ${marketTooltip.pinnedId === market.region_slug ? 'Close' : 'Open'} market details.`}
              onMouseEnter={() => showMarketTooltip(market)}
              onMouseLeave={() => marketTooltip.hide(market.region_slug)}
              onFocus={() => showMarketTooltip(market)}
              onBlur={() => marketTooltip.hide(market.region_slug)}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                countyTooltip.clearAll();
                if (marketTooltip.pinnedId !== market.region_slug) {
                  setMarketTrendView('zillow');
                  setPinnedTooltipLeft(pinnedTooltipViewportLeft(
                    event.currentTarget.getBoundingClientRect(),
                    window.innerWidth,
                  ));
                } else {
                  setPinnedTooltipLeft(null);
                }
                marketTooltip.togglePinned(market.region_slug);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  countyTooltip.clearAll();
                  if (marketTooltip.pinnedId !== market.region_slug) {
                    setMarketTrendView('zillow');
                    setPinnedTooltipLeft(pinnedTooltipViewportLeft(
                      event.currentTarget.getBoundingClientRect(),
                      window.innerWidth,
                    ));
                  } else {
                    setPinnedTooltipLeft(null);
                  }
                  marketTooltip.togglePinned(market.region_slug);
                }
              }}
            >
              {marketLimit !== 'all' ? (
                <circle className="fema-screening-map__market-halo" r="15.5" />
              ) : null}
              <circle
                className={`fema-screening-map__market-core${marketLimit === 'all' ? ' is-compact' : ''}`}
                r={marketLimit === 'all' ? 5.2 : 12}
              />
              {marketLimit !== 'all' ? (
                <text
                  y="3"
                  className={market.rank >= 100
                    ? 'fema-screening-map__market-number--three-digit'
                    : market.rank >= 10
                      ? 'fema-screening-map__market-number--two-digit'
                      : undefined}
                >
                  {market.rank}
                </text>
              ) : null}
            </g>
          ))}
        </svg>
        )}


        <div className="fema-screening-map__legend fema-screening-map__legend--overlay">
          {useGoogleRegionMap ? (
            <><i className="fema-screening-map__region-outline-key" /><span>Region</span></>
          ) : (
            <>
              <span>Lower</span>
              {(selectedCrimeMetric ? CRIME_COLORS : RISK_COLORS).map((color) => <i key={color} style={{ background: color }} />)}
              <span>{selectedCrimeMetric ? 'Higher reported rate' : 'Higher modeled risk'}</span>
            </>
          )}
          {selectedRegion ? <><i className="fema-screening-map__zcta-key" /><span>ZIP area</span></> : <><i className="fema-screening-map__market-key" /><span>Ranked market</span></>}
          {useGoogleRegionMap && sourcingZones.length ? <><i className="fema-screening-map__zone-key" /><span>Drawn area</span></> : null}
        </div>

        {marketTooltip.activeId && marketPoints.find((market) => market.region_slug === marketTooltip.activeId) ? (() => {
          const activeMarket = marketPoints.find((market) => market.region_slug === marketTooltip.activeId);
          const placement = anchoredTooltipPlacement(
            activeMarket,
            DIMENSIONS,
            TOOLTIP_PLACEMENT_OPTIONS,
          );
          const zillowHistory = zillowHistoryBySlug[activeMarket.region_slug];
          const laborSnapshot = laborSnapshotBySlug[activeMarket.region_slug];
          const industryTrends = industryTrendsBySlug[activeMarket.region_slug];
          const alternateTrendName = marketTrendView === 'zillow'
            ? 'Labor Market Trends'
            : 'Zillow Market Trends';
          const marketSelectionId = getMarketSelectionId(activeMarket);
          const marketTargetKey = `research_market:${marketSelectionId}`;
          const marketSelected = selectedSearchTargetKeys.has(marketTargetKey);
          const trendSwitcher = (
            <div
              className="fema-screening-map__trend-switcher"
              role="group"
              aria-label="Switch market trend view"
            >
              <button
                type="button"
                aria-label={`Show previous trend view: ${alternateTrendName}`}
                title={`Show ${alternateTrendName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setMarketTrendView((current) => (current === 'zillow' ? 'labor' : 'zillow'));
                }}
              >
                <FiChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={`Show next trend view: ${alternateTrendName}`}
                title={`Show ${alternateTrendName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setMarketTrendView((current) => (current === 'zillow' ? 'labor' : 'zillow'));
                }}
              >
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>
          );
          return placement ? (
          <div
            className={`fema-screening-map__tooltip fema-screening-map__tooltip--card fema-screening-map__tooltip--market fema-screening-map__tooltip--${placement.placement}${marketTooltip.isPinned ? ' fema-screening-map__tooltip--interactive' : ''}`}
            style={marketTooltip.isPinned ? {
              left: pinnedTooltipLeft == null ? '50%' : `${pinnedTooltipLeft}px`,
            } : {
              left: `${placement.leftPercent}%`,
              top: `${placement.topPercent}%`,
            }}
          >
            <header className="fema-screening-map__market-hero">
              <div className="fema-screening-map__market-name-actions">
                {marketTooltip.isPinned ? (
                  <Link
                    className="fema-screening-map__region-link"
                    title={activeMarket.region_name}
                    to={`/research/markets/${activeMarket.region_slug}`}
                  >
                    {activeMarket.region_name}
                  </Link>
                ) : (
                  <strong title={activeMarket.region_name}>{activeMarket.region_name}</strong>
                )}
                {selectedStateOption && !selectedRegion && typeof onRegionMapOpen === 'function' ? (
                  <button
                    type="button"
                    className="fema-screening-map__region-map-link"
                    aria-label={`Open ${activeMarket.region_name} region map`}
                    onMouseEnter={() => onRegionMapPrefetch?.(activeMarket)}
                    onFocus={() => onRegionMapPrefetch?.(activeMarket)}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      marketTooltip.clearAll();
                      onRegionMapOpen(activeMarket);
                    }}
                  >
                    Region Map
                  </button>
                ) : null}
                {!selectedStateOption && !selectedRegion && activeMarket.state_name && typeof onSelectedStateChange === 'function' ? (
                  <button
                    type="button"
                    className="fema-screening-map__region-map-link"
                    aria-label={`Open ${activeMarket.state_name} state map`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      marketTooltip.clearAll();
                      setSelectedState(activeMarket.state_name);
                      onSelectedStateChange(activeMarket.state_name);
                    }}
                  >
                    State Map
                  </button>
                ) : null}
                {!selectedRegion ? (
                  <NonDisclosureIndicator stateAbbreviation={selectedStateOption?.abbreviation || activeMarket.state_name} />
                ) : null}
              </div>
              <div className="fema-screening-map__market-rank">
                <span>Current rank</span>
                <b>
                  <span>#{activeMarket.rank}</span>
                  <i aria-hidden="true">|</i>
                  <span>{formatResearchMarketRulePercent(getResearchMarketLatestRuleRatio(activeMarket))}</span>
                </b>
              </div>
            </header>
            <div className="fema-screening-map__marker-county">
              <div className="fema-screening-map__population-row">
                <b
                  className={`fema-screening-map__population fema-screening-map__population--${researchMarketPopulationTone(activeMarket.population)}`}
                  title={researchMarketPopulationTitle(activeMarket)}
                  aria-label={researchMarketPopulationTitle(activeMarket)}
                >
                  Population · {formatCompactPopulation(activeMarket.population)}
                </b>
                {typeof onSearchTargetToggle === 'function' ? <div className="fema-screening-map__api-actions"><label>
                  <span>API Credit</span>
                  <input type="checkbox" checked={marketSelected} disabled={!marketSelected && (!isMarketSelectable(activeMarket) || selectedSearchTargetCount >= selectionLimit)} onClick={(event) => event.stopPropagation()} onChange={() => onSearchTargetToggle({ type: 'research_market', id: marketSelectionId, name: activeMarket.region_name })} aria-label={`Select ${activeMarket.region_name} for ${selectionPurpose}`} />
                </label>{marketSelected && typeof onRunSearchTarget === 'function' ? <button type="button" onClick={(event) => { event.stopPropagation(); marketTooltip.clearAll(); onRunSearchTarget({ type: 'research_market', id: marketSelectionId, name: activeMarket.region_name }); }} aria-label={`Run APIs for ${activeMarket.region_name}`}>Run</button> : null}</div> : null}
              </div>
              <div className="fema-screening-map__risk-selection">
                {activeMarket.markerCounty ? (
                  <b className={femaRiskToneClass(activeMarket.markerCounty)}>
                    FEMA Risk · {activeMarket.markerCounty.risk_rating || 'No rating'}
                    {' · '}
                    {formatScore(activeMarket.markerCounty.risk_score)}
                  </b>
                ) : <span />}
              </div>
              {activeMarket.markerCounty ? (
                <TopHazardDrivers
                  county={activeMarket.markerCounty}
                  showBars
                />
              ) : null}
            </div>
            {marketTooltip.isPinned ? (
              marketTrendView === 'zillow' ? (
                <ZillowTabbedTimelineDemo
                  variant="compact"
                  headerControls={trendSwitcher}
                  loading={zillowHistory?.loading ?? true}
                  error={zillowHistory?.error || ''}
                  payload={zillowHistory?.payload || null}
                  onRetry={() => setTrendRetryNonce((value) => value + 1)}
                />
              ) : (
                <LaborTabbedChartDemo
                  variant="compact"
                  headerControls={trendSwitcher}
                  loading={laborSnapshot?.loading ?? true}
                  error={laborSnapshot?.error || ''}
                  snapshot={laborSnapshot?.payload || null}
                  industryLoading={industryTrends?.loading ?? false}
                  industryError={industryTrends?.error || ''}
                  industryPayload={industryTrends?.payload || null}
                  onIndustryRequested={() => requestIndustryTrends(activeMarket.region_slug)}
                  onRetry={() => {
                    setTrendRetryNonce((value) => value + 1);
                    if (industryTrends?.error) requestIndustryTrends(activeMarket.region_slug);
                  }}
                />
              )
            ) : null}
          </div>
          ) : null;
        })() : activeZcta && !zipTooltipsDisabled ? (() => {
          const properties = activeZcta.properties || {};
          const zcta5 = activeZcta.geoId;
          const zctaState = properties.state_abbr || selectedRegion?.state_name;
          const targetKey = `zcta:${zctaState}:${zcta5}`;
          const selected = selectedSearchTargetKeys.has(targetKey);
          const localityLabel = properties.primary_locality || `ZIP ${zcta5}`;
          const placement = anchoredTooltipPlacement(
            useGoogleRegionMap ? googleZctaAnchors[zcta5] : activeZcta,
            DIMENSIONS,
            TOOLTIP_PLACEMENT_OPTIONS,
          );
          return placement ? (
            <div
              className={`fema-screening-map__tooltip fema-screening-map__tooltip--card fema-screening-map__tooltip--county fema-screening-map__tooltip--${placement.placement}${zctaTooltip.isPinned ? ' fema-screening-map__tooltip--interactive' : ''}`}
              style={{ left: `${placement.leftPercent}%`, top: `${placement.topPercent}%` }}
            >
              <header className="fema-screening-map__market-hero fema-screening-map__market-hero--unranked">
                <strong>{properties.primary_locality ? `${localityLabel}, ${zctaState} · ${zcta5}` : localityLabel}</strong>
              </header>
              <div className="fema-screening-map__marker-county fema-screening-map__marker-county--unranked">
                <div className="fema-screening-map__population-row">
                  <b>Population · {formatCompactPopulation(properties.population_2020)}</b>
                  {typeof onSearchTargetToggle === 'function' ? <div className="fema-screening-map__api-actions"><label>
                    <span>API Credit</span>
                    <input type="checkbox" checked={selected} disabled={!selected && selectedSearchTargetCount >= selectionLimit} onClick={(event) => event.stopPropagation()} onChange={() => onSearchTargetToggle({ type: 'zcta', id: zcta5, zcta5, state_abbr: zctaState, name: properties.primary_locality ? `${properties.primary_locality} · ${zcta5}` : `ZIP ${zcta5}` })} aria-label={`Select ${localityLabel} ZIP ${zcta5} for ${selectionPurpose}`} />
                  </label></div> : null}
                </div>
                <small>{properties.housing_units_2020 == null ? 'Housing units unavailable' : `${Number(properties.housing_units_2020).toLocaleString()} housing units`} · Census ZCTA</small>
              </div>
            </div>
          ) : null;
        })() : activeCounty ? (
          (() => {
            const countyShape = countyShapeByFips.get(activeCounty.geo_id);
            const countyTargetKey = `fema_county:${activeCounty.geo_id}`;
            const countySelected = selectedSearchTargetKeys.has(countyTargetKey);
            const placement = anchoredTooltipPlacement(
              countyShape,
              DIMENSIONS,
              TOOLTIP_PLACEMENT_OPTIONS,
            );
            return placement ? (
          <div
            className={`fema-screening-map__tooltip fema-screening-map__tooltip--card fema-screening-map__tooltip--county fema-screening-map__tooltip--${placement.placement}${countyTooltip.isPinned ? ' fema-screening-map__tooltip--interactive' : ''}`}
            style={{ left: `${placement.leftPercent}%`, top: `${placement.topPercent}%` }}
          >
            <header className="fema-screening-map__market-hero fema-screening-map__market-hero--unranked">
              {selectedStateOption && !selectedRegion && activeCounty.name && typeof onRegionMapOpen === 'function' ? (
                <div className="fema-screening-map__market-name-actions">
                  <strong title={countyLabel(activeCounty)}>{countyLabel(activeCounty)}</strong>
                  <button
                    type="button"
                    className="fema-screening-map__region-map-link"
                    aria-label={`Open ${countyLabel(activeCounty)} region map`}
                    onMouseEnter={() => onRegionMapPrefetch?.({
                      region_slug: `county-${activeCounty.geo_id}`,
                      state_name: activeCounty.state_abbr || selectedState,
                      market_county_fips: [activeCounty.geo_id],
                      marker_county_fips: activeCounty.geo_id,
                    })}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      countyTooltip.clearAll();
                      onRegionMapOpen({
                        region_slug: `county-${activeCounty.geo_id}`,
                        region_name: countyLabel(activeCounty),
                        state_name: activeCounty.state_abbr || selectedState,
                        market_county_fips: [activeCounty.geo_id],
                        marker_county_fips: activeCounty.geo_id,
                      });
                    }}
                  >
                    Region Map
                  </button>
                  <NonDisclosureIndicator stateAbbreviation={selectedStateOption.abbreviation} />
                </div>
              ) : !selectedStateOption && !selectedRegion && activeCounty.state_abbr && typeof onSelectedStateChange === 'function' ? (
                <div className="fema-screening-map__market-name-actions">
                  <strong title={countyLabel(activeCounty)}>{countyLabel(activeCounty)}</strong>
                  <button
                    type="button"
                    className="fema-screening-map__region-map-link"
                    aria-label={`Open ${activeCounty.state_abbr} state map`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      countyTooltip.clearAll();
                      setSelectedState(activeCounty.state_abbr);
                      onSelectedStateChange(activeCounty.state_abbr);
                    }}
                  >
                    State Map
                  </button>
                  <NonDisclosureIndicator stateAbbreviation={activeCounty.state_abbr} />
                </div>
              ) : (
                <strong>{countyLabel(activeCounty)}</strong>
              )}
            </header>
            <div className="fema-screening-map__marker-county fema-screening-map__marker-county--unranked">
              <div className="fema-screening-map__population-row">
                <b
                  className={`fema-screening-map__population fema-screening-map__population--${researchMarketPopulationTone(activeCounty.population)}`}
                  title={femaCountyPopulationTitle(activeCounty, payload?.release)}
                  aria-label={femaCountyPopulationTitle(activeCounty, payload?.release)}
                >
                  Population · {formatCompactPopulation(activeCounty.population)}
                </b>
                {typeof onSearchTargetToggle === 'function' ? <div className="fema-screening-map__api-actions"><label>
                  <span>API Credit</span>
                  <input type="checkbox" checked={countySelected} disabled={!countySelected && (!activeCounty.api_search_target || selectedSearchTargetCount >= selectionLimit)} onClick={(event) => event.stopPropagation()} onChange={() => onSearchTargetToggle({ type: 'fema_county', fips: activeCounty.geo_id, name: countyLabel(activeCounty) })} aria-label={`Select ${countyLabel(activeCounty)} for ${selectionPurpose}`} title={activeCounty.api_search_target ? undefined : 'Canonical county coordinates are not available.'} />
                </label>{countySelected && typeof onRunSearchTarget === 'function' ? <button type="button" onClick={(event) => { event.stopPropagation(); countyTooltip.clearAll(); onRunSearchTarget({ type: 'fema_county', fips: activeCounty.geo_id, name: countyLabel(activeCounty) }); }} aria-label={`Run APIs for ${countyLabel(activeCounty)}`}>Run</button> : null}</div> : null}
              </div>
              {selectedCrimeMetric ? (
                <><div className="fema-screening-map__risk-selection">
                  <b>
                    {payload.selected_metric?.label || 'Crime'} · {activeCounty.source_state === 'complete'
                      ? `${Math.round(activeCounty.crime_rate_per_100k).toLocaleString()} per 100,000`
                      : 'No published rate'}
                  </b>
                </div><small>{activeCounty.source_state === 'complete'
                  ? `${activeCounty.population_coverage_percent?.toFixed?.(1) || activeCounty.population_coverage_percent}% population coverage · ${activeCounty.contributing_agency_count || 0} agencies · ${activeCounty.source_year}`
                  : `${String(activeCounty.source_state || 'no_data').replaceAll('_', ' ')} · Blank is not a low-crime rating`}</small></>
              ) : <div className="fema-screening-map__risk-selection">
                <b className={femaRiskToneClass(activeCounty)}>
                  FEMA Risk · {activeCounty.risk_rating || 'No rating'} · {formatScore(activeCounty.risk_score)}
                </b>
              </div>}
              {!selectedCrimeMetric && selectedHazard === 'overall' && activeCounty.top_hazards?.length ? (
                <TopHazardDrivers county={activeCounty} showBars />
              ) : !selectedCrimeMetric ? (
                <small>
                  {formatCompactCurrency(activeCounty.expected_annual_loss)}
                  {' modeled annual loss'}
                </small>
              ) : null}
            </div>
          </div>
            ) : null;
          })()
        ) : null}
      </div>
      {selectedRegion && compGuidanceOpen ? (
        <div className="fema-screening-map__comp-guidance-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setCompGuidanceOpen(false)}>
          <section className="fema-screening-map__comp-guidance-dialog" role="dialog" aria-modal="true" aria-labelledby="comp-guidance-title">
            <header>
              <div><small>Region-map reference</small><h2 id="comp-guidance-title">Comparable review guide</h2></div>
              <button type="button" aria-label="Close comparable review guide" onClick={() => setCompGuidanceOpen(false)}><i className="pi pi-times" aria-hidden="true" /></button>
            </header>
            <p>Agency-aligned review framework plus a ready-to-paste Browser AI instruction. Always verify the linked current guides before a lending decision.</p>
            <textarea
              value={compGuidanceDraft}
              onChange={(event) => { setCompGuidanceDraft(event.target.value); setCompGuidanceSaved(false); }}
              aria-label="Editable comparable review guidance"
            />
            <footer>
              <a href="https://selling-guide.fanniemae.com/" target="_blank" rel="noreferrer">Fannie Mae guide</a>
              <a href="https://guide.freddiemac.com/" target="_blank" rel="noreferrer">Freddie Mac guide</a>
              <button type="button" className="is-save" disabled={compGuidanceSaved} onClick={saveCompGuidance}><i className="pi pi-save" aria-hidden="true" />{compGuidanceSaved ? 'Saved' : 'Save changes'}</button>
              <button type="button" onClick={copyCompGuidance}><i className={`pi ${compGuidanceCopied ? 'pi-check' : 'pi-copy'}`} aria-hidden="true" />{compGuidanceCopied ? 'Copied' : 'Copy all'}</button>
            </footer>
          </section>
        </div>
      ) : null}
      {selectedRegion && boundaryReviewOpen && boundaryExperiment ? (
        <div className="fema-screening-map__comp-guidance-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setBoundaryReviewOpen(false)}>
          <section className="fema-screening-map__boundary-review-dialog" role="dialog" aria-modal="true" aria-labelledby="boundary-review-title">
            <header><div><small>Marion experiment evidence</small><h2 id="boundary-review-title">Review high-lead areas</h2></div><button type="button" aria-label="Close boundary review" onClick={() => setBoundaryReviewOpen(false)}><i className="pi pi-times" /></button></header>
            <p>These are proposed starting boundaries, not approved appraisal neighborhoods. Compare roads, barriers, housing stock, sold evidence, rent evidence, and edit time.</p>
            <div className="fema-screening-map__boundary-review-grid">
              {[...(boundaryExperiment.comparison_stats?.geoapify?.top_five_lead_areas || []).map((area) => ({ ...area, source: 'geoapify' })), ...(boundaryExperiment.comparison_stats?.overture?.top_five_lead_areas || []).map((area) => ({ ...area, source: 'overture' }))].sort((left, right) => (right.counts?.for_sale || 0) - (left.counts?.for_sale || 0)).slice(0, 5).map((area, index) => (
                <label key={`${area.source}-${area.feature_id}-${index}`}><span><b>{index + 1}. {area.name}</b><small>{area.source === 'geoapify' ? 'Geoapify' : 'Overture'} · {area.counts?.for_sale || 0} leads · {area.counts?.sold || 0} sold · {area.counts?.rentals || 0} rentals</small>{boundaryExperiment.selected_winner === area.source ? <button type="button" onClick={() => importBoundaryArea(area.source, area)}>Import winning polygon as draft</button> : null}</span><textarea value={boundaryReviewNotes[area.feature_id] || ''} onChange={(event) => setBoundaryReviewNotes((current) => ({ ...current, [area.feature_id]: event.target.value }))} placeholder="Corrections, barriers, housing-stock fit…" /></label>
              ))}
            </div>
            <footer><label>Result<select aria-label="Boundary comparison winner" value={boundaryWinner} onChange={(event) => setBoundaryWinner(event.target.value)}><option value="">Choose result</option><option value="geoapify">Geoapify</option><option value="overture">Overture</option><option value="tie">Tie</option><option value="neither">Neither is usable</option></select></label><button type="button" onClick={saveBoundaryReview} disabled={!boundaryWinner}>Save review</button></footer>
          </section>
        </div>
      ) : null}

      {regionError ? <p className="fema-screening-map__error">{regionError}</p> : null}
      {selectedRegion && googleMapUnavailable ? <p className="fema-screening-map__error">Street map unavailable ({googleMapUnavailable}). Showing the standard region map.</p> : null}

      {showSourceAudit ? <MapSourceAuditDropdown datasetKeys={sourceAuditKeys} /> : null}
    </section>
  );
}
